import React, { useState, useEffect, useRef } from 'react';
import {
  TrendingUp, TrendingDown, Wifi, WifiOff, RefreshCw,
  X, Plus, Pause, Play, Pin, LayoutGrid, Search, Maximize2, Minimize2,
} from 'lucide-react';
import './RRTVMarketPage.css';
import { useLayoutMaster } from '../utils/useLayoutMaster';
import { RRTVPlayer } from './RRTVPlayer';
import { Header } from '../components/Header';
import { ReactComponent as LogoRRTV }   from '../assets/logos/LOGO RR TV.svg';
import { ReactComponent as LogoMagma }  from '../assets/logos/LOGO RR MAGMA.svg';
import Silk from '../components/Silk';

// ── RRTV modules (see pages/rrtv/) ──
import {
  API_URL, WS_URL, DINO_URL,
  fmtNum, fmtVol, fmtTime, fmtAssetPrice, baseSymbol, tzClock, brtMinutes,
} from './rrtv/rrtvFormat';
import {
  getTodaySlots, fmtSlotTime, buildExchangeMap, useExchangeRotation, PHASE_COMMERCIAL,
} from './rrtv/rrtvExchanges';
import { TickerLogo, PriceCard } from './rrtv/TickerLogo';
import { AssetSearch, AssetDrill } from './rrtv/AssetDetail';
import { ResearchTab, CustomPane } from './rrtv/ResearchTab';

// ---------- MagmaCommercial ----------
function MagmaCommercial() {
  const [count, setCount] = useState(3847);
  useEffect(() => {
    const id = setInterval(() => setCount(c => c + Math.floor((Math.random() - 0.3) * 5)), 2000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="rrtv-commercial">
      <div className="rrtv-commercial-bg" />
      <div className="rrtv-commercial-inner">

        {/* Left — branding */}
        <div className="rrtv-commercial-brand">
          <div className="rrtv-commercial-eyebrow">RELATÓRIO RESERVADO · PLATAFORMA DE DADOS</div>
          <LogoMagma className="rrtv-commercial-logo" />
          <p className="rrtv-commercial-tag">
            Profundidade de mercado.<br/>
            <span>Em tempo real.</span>
          </p>
          <p className="rrtv-commercial-sub">
            Order book L2 · COPOM · índices globais · 60k+ artigos RR
          </p>
          <div className="rrtv-commercial-users">
            <div className="rrtv-commercial-avatars">
              {['#6660fe','#8b87ff','#FFB300','#22c55e'].map((c,i) => (
                <div key={i} className="rrtv-commercial-avatar" style={{ background: c, marginLeft: i > 0 ? -7 : 0 }}>
                  {['A','B','C','D'][i]}
                </div>
              ))}
            </div>
            <span className="rrtv-commercial-count">
              <strong>{count.toLocaleString('pt-BR')}</strong> conectados agora
            </span>
          </div>
        </div>

        {/* Right — stats grid + CTA */}
        <div className="rrtv-commercial-right">
          <div className="rrtv-commercial-stats">
            {[
              { l: 'FONTES',    v: '89',     s: 'sistêmicas', c: '#6660fe' },
              { l: 'COBERTURA', v: 'G20',    s: 'mercados',   c: '#8b87ff' },
              { l: 'LATÊNCIA',  v: '<50ms',  s: 'ao vivo',    c: '#FFB300' },
              { l: 'ATIVOS',    v: '1.955',  s: 'instrumentos', c: '#22c55e' },
            ].map(m => (
              <div key={m.l} className="rrtv-commercial-stat">
                <div className="rrtv-commercial-stat-label">{m.l}</div>
                <div className="rrtv-commercial-stat-value" style={{ color: m.c }}>{m.v}</div>
                <div className="rrtv-commercial-stat-sub">{m.s}</div>
              </div>
            ))}
          </div>
          <div className="rrtv-commercial-cta-row">
            <a href="https://magma.relatorioreservado.com.br" target="_blank" rel="noopener noreferrer"
              className="rrtv-commercial-cta-primary">
              ENTRAR NO RR MAGMA →
            </a>
            <span className="rrtv-commercial-tier">
              Plano gratuito disponível · Tier Pro desbloqueia mais dados
            </span>
          </div>
        </div>

      </div>
    </div>
  );
}

// ---------- ExchangeTicker (footer) ----------
function ExchangeTicker({ exchMap, phase, current, fading: parentFading }) {
  const [tickClock, setTickClock] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTickClock(t => t + 1), 10000);
    return () => clearInterval(id);
  }, []);

  if (!current) return null;
  const localTime = current.tz ? tzClock(current.tz) : null;
  const items = current.tape || [];

  return (
    <div className="rrtv-ticker">
      <div className={`rrtv-ticker-exch-pill ${current.isOpen ? 'open' : 'closed'}`}>
        <span className="rrtv-ticker-exch-flag">{current.flag}</span>
        <span className="rrtv-ticker-exch-name">{current.label}</span>
        {localTime && <span className="rrtv-ticker-exch-time">{localTime}</span>}
        <span className="rrtv-ticker-exch-dot">{current.isOpen ? '●' : '○'}</span>
      </div>
      <div className={`rrtv-ticker-fade-wrap ${parentFading ? 'fading' : ''}`}>
        <div className="rrtv-ticker-track">
          {[0,1,2].map(dup => (
            <div key={dup} className="rrtv-ticker-group">
              {items.map((t, i) => {
                const up = (t.change ?? 0) >= 0;
                return (
                  <span key={`${dup}-${i}`} className="rrtv-ticker-item">
                    <TickerLogo ticker={t.ticker} size={14} className="rrtv-ticker-logo" />
                    <b>{t.ticker}</b>
                    {t.currency && <span className="rrtv-ticker-currency">{t.currency} </span>}
                    {t.price != null ? fmtNum(t.price) : '—'}
                    <span className={up ? 'pos' : 'neg'}>
                      {up ? ' ▲ +' : ' ▼ '}{Math.abs(t.change ?? 0).toFixed(2)}%
                    </span>
                  </span>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- ExchangeRail — click a flag to pin/freeze on that market ----------
function ExchangeRail({ exchMap, currentId, pinnedId, onPin }) {
  if (!exchMap.length) return null;
  return (
    <div className="rrtv-exch-rail" role="tablist" aria-label="Praças">
      {exchMap.map(e => {
        const active  = e.id === currentId;
        const pinned  = e.id === pinnedId;
        return (
          <button
            key={e.id}
            className={`rrtv-exch-rail-chip${active ? ' active' : ''}${pinned ? ' pinned' : ''}${e.isOpen ? ' open' : ''}`}
            onClick={() => onPin(pinned ? null : e.id)}
            title={pinned ? 'Soltar' : `Fixar ${e.label}`}
          >
            <span className="rrtv-exch-rail-flag">{e.flag}</span>
            <span className="rrtv-exch-rail-name">{e.label}</span>
            {pinned && <Pin size={9} className="rrtv-exch-rail-pin" />}
          </button>
        );
      })}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// AlertaBoard — the monitor surface (TV feed made navigable/interactive)
// ══════════════════════════════════════════════════════════════════
function AlertaBoard({ data, isMobile, variant = 'full', onImmersive }) {
  const {
    marketData, dinoFlat, curMins, schedule,
    isConnected, lastUpdate, fetchData,
  } = data;

  const [paused,   setPaused]   = useState(false);
  const [pinnedId, setPinnedId] = useState(null);
  const [drill,    setDrill]    = useState(null);
  const [overrides,  setOverrides]  = useState({});   // slotKey -> user-pinned asset
  const [slotSearch, setSlotSearch] = useState(null); // { key } while choosing a swap
  const holdRef = useRef(false);

  // Drilling into an asset = a deeper layer → collapse chrome (immersive)
  useEffect(() => { onImmersive?.(!!drill); }, [drill, onImmersive]);

  const b3Gainers = marketData?.topGainers       ?? [];
  const b3Losers  = marketData?.topLosers        ?? [];
  const b3Active  = marketData?.mostActive       ?? [];
  const wkGainers = marketData?.topWeeklyGainers ?? [];
  const wkLosers  = marketData?.topWeeklyLosers  ?? [];

  const exchMap = buildExchangeMap(b3Gainers, b3Losers, b3Active, dinoFlat, curMins);

  const frozen = paused || !!pinnedId || !!drill || !!slotSearch;
  const { phase, current, fading, primaryOpen } = useExchangeRotation(exchMap, frozen, pinnedId);

  const displayGainers = current?.gainers ?? [];
  const displayLosers  = current?.losers  ?? [];
  const displayMovers  = current?.movers  ?? [];
  const displayHero    = displayMovers[0];
  const displayHeatmap = displayMovers.slice(1, 10);
  const isB3           = current?.id === 'B3';
  const footerTrio     = (current?.anchors ?? displayMovers).slice(0, 3); // bellwethers of the shown exchange

  const openDrill = (t) => setDrill({ ...t });
  const showCommercial = phase === PHASE_COMMERCIAL && !frozen;

  // Override + press-and-hold: tap = open detail, hold (~500ms) = swap this slot's asset
  const ov = (key, item) => (overrides[key] ? overrides[key] : item);
  const press = (key, item) => {
    let timer = null;
    const cancel = () => { if (timer) { clearTimeout(timer); timer = null; } };
    return {
      role: 'button', tabIndex: 0,
      onPointerDown: () => { holdRef.current = false; timer = setTimeout(() => { holdRef.current = true; setSlotSearch({ key }); }, 500); },
      onPointerUp: cancel,
      onPointerLeave: cancel,
      onPointerMove: cancel, // moving = scrolling, not a hold
      onContextMenu: (e) => e.preventDefault(),
      onClick: () => { if (holdRef.current) { holdRef.current = false; return; } openDrill(item); },
    };
  };
  const chooseSlot = (chosen) => {
    if (!slotSearch) return;
    setOverrides(o => ({ ...o, [slotSearch.key]: { ...chosen, ult: chosen.price, var: chosen.change, price: chosen.price, change: chosen.change } }));
    setSlotSearch(null);
  };
  const resetSlot = () => {
    if (!slotSearch) return;
    setOverrides(o => { const n = { ...o }; delete n[slotSearch.key]; return n; });
    setSlotSearch(null);
  };

  return (
    <>
      {/* ---------- rail: pick / pin a market (freezes the giro) ---------- */}
      <ExchangeRail
        exchMap={exchMap}
        currentId={current?.id}
        pinnedId={pinnedId}
        onPin={setPinnedId}
      />

      {/* ============ MAIN GRID ============ */}
      <div className="rrtv-main">

        {/* ---------- LEFT: RR TV ZONE ---------- */}
        <section className="rrtv-zone">
          <div className="rrtv-visual">
            <RRTVPlayer variant="inline" />
          </div>
          <div className="rrtv-schedule">
            {schedule.map((s, i) => (
              <div key={i} className={`rrtv-schedule-cell status-${s.status}`}>
                <div className="rrtv-schedule-time">{fmtSlotTime(s.start)}</div>
                <div className="rrtv-schedule-show">{s.badge} {s.show}</div>
                <div className="rrtv-schedule-badge">
                  {s.status === 'live'     && '● AO VIVO'}
                  {s.status === 'next'     && 'PRÓXIMO'}
                  {s.status === 'done'     && 'EXIBIDO'}
                  {s.status === 'upcoming' && '—'}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ---------- RIGHT: MARKET DATA ---------- */}
        <section className="rrtv-market">

          {/* Exchange context bar — current exchange + freeze control */}
          <div className={`rrtv-exch-bar ${fading ? 'fading' : ''}`}>
            <span className="rrtv-exch-bar-flag">{current?.flag}</span>
            <span className="rrtv-exch-bar-name">{current?.label}</span>
            {current?.tz && <span className="rrtv-exch-bar-time">{tzClock(current.tz)}</span>}
            <span className={`rrtv-exch-bar-dot ${current?.isOpen ? 'open' : 'closed'}`}>
              {current?.isOpen ? '● ABERTO' : '○ FECHADO'}
            </span>

            <div className="rrtv-exch-bar-ctrls">
              {frozen
                ? <span className="rrtv-live-tag frozen"><Pause size={10} /> PAUSADO</span>
                : <span className="rrtv-live-tag"><span className="rrtv-live-dot" /> AO VIVO</span>}
              <button
                className="rrtv-freeze-btn"
                onClick={() => { setPaused(p => !p); if (pinnedId) setPinnedId(null); }}
                title={frozen ? 'Retomar giro' : 'Pausar giro'}
              >
                {frozen ? <Play size={12} /> : <Pause size={12} />}
                {frozen ? 'Retomar' : 'Pausar'}
              </button>
            </div>

            {showCommercial && (
              <span className="rrtv-exch-bar-ad">MENSAGEM PATROCINADA</span>
            )}
          </div>

          {/* COMMERCIAL interstitial — only while auto-cycling */}
          {showCommercial ? (
            <MagmaCommercial />
          ) : (
            <>
              {/* Gainers / Losers chips — tap to drill, hold to swap */}
              <div className={`rrtv-chips-row ${fading ? 'fading' : ''}`}>
                <div className="rrtv-chip-box">
                  <div className="rrtv-chip-header">
                    <TrendingUp size={14} className="pos" />
                    TOP GAINERS · {current?.flag} {current?.label}
                  </div>
                  <div className="rrtv-chip-grid">
                    {displayGainers.slice(0, 6).map((t, i) => {
                      const key = `gain:${current?.id}:${i}`; const it = ov(key, t);
                      return (
                        <div key={i} className="rrtv-chip pos rrtv-clickable" {...press(key, it)}>
                          <span>{it.ticker}</span>
                          <span>{(it.change ?? it.var ?? 0) >= 0 ? '+' : ''}{(it.change ?? it.var ?? 0).toFixed(2)}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="rrtv-chip-box">
                  <div className="rrtv-chip-header">
                    <TrendingDown size={14} className="neg" />
                    TOP LOSERS · {current?.flag} {current?.label}
                  </div>
                  <div className="rrtv-chip-grid">
                    {displayLosers.slice(0, 6).map((t, i) => {
                      const key = `lose:${current?.id}:${i}`; const it = ov(key, t);
                      return (
                        <div key={i} className="rrtv-chip neg rrtv-clickable" {...press(key, it)}>
                          <span>{it.ticker}</span>
                          <span>{(it.change ?? it.var ?? 0) >= 0 ? '+' : ''}{(it.change ?? it.var ?? 0).toFixed(2)}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Hero + Heatmap — tap to drill, hold to swap the asset */}
              <div className={`rrtv-heatmap-row ${fading ? 'fading' : ''}`}>
                <div className="rrtv-hero-col">
                  {displayHero && (() => {
                    const key = `hero:${current?.id}`; const it = ov(key, displayHero);
                    return <PriceCard t={it} size="large" pressProps={press(key, it)} />;
                  })()}
                </div>
                <div className="rrtv-heatmap-grid">
                  {displayHeatmap.map((t, i) => {
                    const key = `heat:${current?.id}:${i}`; const it = ov(key, t);
                    return <PriceCard key={i} t={it} pressProps={press(key, it)} />;
                  })}
                </div>
              </div>

              {/* Weekly strip — B3 shows weekly, others show top movers */}
              <div className={`rrtv-weekly-row ${fading ? 'fading' : ''}`}>
                {isB3 ? (
                  <>
                    <div className="rrtv-weekly-box">
                      <div className="rrtv-weekly-header pos">ALTAS DA SEMANA · B3</div>
                      {wkGainers.slice(0, 5).map((t) => (
                        <div key={t.ticker} className="rrtv-weekly-row-item rrtv-clickable"
                          onClick={() => openDrill({ ...t, ticker: baseSymbol(t.ticker), fullTicker: t.ticker, price: t.ult, change: t.var, currency: 'R$' })}
                          role="button" tabIndex={0}>
                          <TickerLogo ticker={t.ticker} size={16} />
                          <span className="rrtv-weekly-tk">{baseSymbol(t.ticker)}</span>
                          <span className="rrtv-weekly-nm">{t.name}</span>
                          <span className="rrtv-weekly-var pos">+{(t.sem ?? 0).toFixed(2)}%</span>
                        </div>
                      ))}
                    </div>
                    <div className="rrtv-weekly-box">
                      <div className="rrtv-weekly-header neg">BAIXAS DA SEMANA · B3</div>
                      {wkLosers.slice(0, 5).map((t) => (
                        <div key={t.ticker} className="rrtv-weekly-row-item rrtv-clickable"
                          onClick={() => openDrill({ ...t, ticker: baseSymbol(t.ticker), fullTicker: t.ticker, price: t.ult, change: t.var, currency: 'R$' })}
                          role="button" tabIndex={0}>
                          <TickerLogo ticker={t.ticker} size={16} />
                          <span className="rrtv-weekly-tk">{baseSymbol(t.ticker)}</span>
                          <span className="rrtv-weekly-nm">{t.name}</span>
                          <span className="rrtv-weekly-var neg">{(t.sem ?? 0).toFixed(2)}%</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="rrtv-weekly-box">
                      <div className="rrtv-weekly-header pos">ALTAS DO DIA · {current?.flag} {current?.label}</div>
                      {displayGainers.slice(0, 5).map((t) => (
                        <div key={t.ticker} className="rrtv-weekly-row-item rrtv-clickable"
                          onClick={() => openDrill(t)} role="button" tabIndex={0}>
                          <TickerLogo ticker={t.ticker} size={16} />
                          <span className="rrtv-weekly-tk">{t.ticker}</span>
                          <span className="rrtv-weekly-nm">{t.name || ''}</span>
                          <span className="rrtv-weekly-var pos">+{(t.change ?? 0).toFixed(2)}%</span>
                        </div>
                      ))}
                    </div>
                    <div className="rrtv-weekly-box">
                      <div className="rrtv-weekly-header neg">BAIXAS DO DIA · {current?.flag} {current?.label}</div>
                      {displayLosers.slice(0, 5).map((t) => (
                        <div key={t.ticker} className="rrtv-weekly-row-item rrtv-clickable"
                          onClick={() => openDrill(t)} role="button" tabIndex={0}>
                          <TickerLogo ticker={t.ticker} size={16} />
                          <span className="rrtv-weekly-tk">{t.ticker}</span>
                          <span className="rrtv-weekly-nm">{t.name || ''}</span>
                          <span className="rrtv-weekly-var neg">{(t.change ?? 0).toFixed(2)}%</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </section>
      </div>

      {/* ============ BOTTOM TRIO — tap to drill, hold to swap ============ */}
      <div className="rrtv-trio">
        {footerTrio.map((t, i) => {
          const key = `trio:${current?.id}:${i}`;
          const it = ov(key, t);
          const up = (it.change ?? it.var ?? 0) >= 0;
          const ccy = it.currency === 'R$' ? 'BRL' : it.currency;
          return (
            <div key={i} className="rrtv-trio-tile rrtv-clickable" {...press(key, it)}>
              <TickerLogo ticker={it.ticker} size={44} className="rrtv-trio-logo" />
              <div>
                <div className="rrtv-trio-label">{it.ticker}{it.vol ? ` · ${fmtVol(it.vol)}` : ''}</div>
                <div className="rrtv-trio-price">{fmtAssetPrice(it.price ?? it.ult, ccy)}</div>
              </div>
              <div className={`rrtv-trio-var ${up ? 'pos' : 'neg'}`}>
                {up ? '+' : ''}{(it.change ?? it.var ?? 0).toFixed(2)}%
              </div>
            </div>
          );
        })}
      </div>

      {/* ============ TICKER FOOTER ============ */}
      {!isMobile && (
        <ExchangeTicker
          exchMap={exchMap}
          phase={phase}
          current={current}
          fading={fading}
        />
      )}

      {/* ============ ASSET DRILL (click-to-drill, self-contained, in place) ============ */}
      {drill && (
        <AssetDrill asset={drill} marketData={marketData} onClose={() => setDrill(null)} />
      )}

      {/* ============ SLOT SWAP (press-and-hold a card → change its asset) ============ */}
      {slotSearch && (
        <>
          <div className="rrtv-drill-scrim" onClick={() => setSlotSearch(null)} />
          <div className="rrtv-slotsearch" role="dialog" aria-label="Trocar ativo do slot">
            <div className="rrtv-slotsearch-head">
              <span className="rrtv-slotsearch-title"><Search size={12} /> FIXAR ATIVO NESTE CARD</span>
              <button className="rrtv-drill-x" onClick={() => setSlotSearch(null)} aria-label="Fechar"><X size={15} /></button>
            </div>
            <AssetSearch marketData={marketData} onSelect={chooseSlot} autoFocus placeholder="Buscar ativo para fixar aqui…" />
            {overrides[slotSearch.key] && (
              <button className="rrtv-slotsearch-reset" onClick={resetSlot}>↺ Restaurar ativo original</button>
            )}
            <div className="rrtv-slotsearch-hint">Segure em qualquer card para trocar · toque simples abre o detalhe</div>
          </div>
        </>
      )}
    </>
  );
}

// ==================================================================
function RRTVMarketPage() {
  const layout = useLayoutMaster();
  const [marketData,  setMarketData]  = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate,  setLastUpdate]  = useState(null);
  const [isDark,      setIsDark]      = useState(true);
  const [dinoFlat,    setDinoFlat]    = useState([]);
  const [schedule,    setSchedule]    = useState(getTodaySlots());
  const [curMins,     setCurMins]     = useState(brtMinutes());

  // Tabs: base Alerta + Research, plus user-created custom tabs
  const [activeTab, setActiveTab]   = useState('alerta');
  const [customTabs, setCustomTabs] = useState([]); // { id, name, widgets, seq }
  const [immersive, setImmersive]   = useState(false); // collapse chrome on deep layers
  const nextTabRef = useRef(1);

  // Leaving a tab drops out of immersive mode
  useEffect(() => { setImmersive(false); }, [activeTab]);

  const theme = {
    background: 'rgba(18,18,18,0.8)', textPrimary: '#fff', textSecondary: '#999',
    border: 'rgba(212,175,55,0.2)', cardBg: 'rgba(18,18,18,0.8)',
  };

  useEffect(() => {
    const tick = () => { setSchedule(getTodaySlots()); setCurMins(brtMinutes()); };
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const r = await fetch(`${API_URL}/api/overview`);
      const d = await r.json();
      setMarketData(d); setLastUpdate(new Date(d.lastUpdated));
    } catch (e) { console.error('fetch error', e); }
  };

  // B3 WebSocket
  useEffect(() => {
    const ws = new WebSocket(WS_URL);
    ws.onopen    = () => setIsConnected(true);
    ws.onclose   = () => setIsConnected(false);
    ws.onerror   = () => setIsConnected(false);
    ws.onmessage = (e) => {
      try { const d = JSON.parse(e.data); setMarketData(d); setLastUpdate(new Date(d.lastUpdated)); } catch {}
    };
    return () => ws.close();
  }, []);

  // DINO REST poll — the FULL worldwide universe (~1.9k names), which feeds
  // every non-B3 board bucketed by ticker suffix. (The old /api/overview only
  // returned the global top-movers, so most exchanges came up empty.)
  useEffect(() => {
    const fetchDino = async () => {
      try {
        const r = await fetch(`${DINO_URL}/api/tickers`);
        const d = await r.json();
        const universe = Array.isArray(d) ? d : (d?.tickers || []);
        setDinoFlat(universe);
      } catch (e) { console.warn('DINO fetch error', e); }
    };
    fetchDino();
    const id = setInterval(fetchDino, 60000);
    return () => clearInterval(id);
  }, []);

  const b3Gainers = marketData?.topGainers ?? [];
  const b3Losers  = marketData?.topLosers  ?? [];
  const topTickers = [...b3Gainers, ...b3Losers].slice(0, 10);

  // Shared data bundle handed to every Alerta surface
  const boardData = {
    marketData, dinoFlat, curMins, schedule,
    isConnected, lastUpdate, fetchData,
  };

  const addTab = () => {
    const id = `tab-${nextTabRef.current++}`;
    setCustomTabs(ts => [...ts, { id, name: `Painel ${ts.length + 1}`, widgets: [], seq: 0 }]);
    setActiveTab(id);
  };

  const updateTab = (next) => {
    setCustomTabs(ts => ts.map(t => t.id === next.id ? next : t));
  };

  const closeTab = (tabId) => {
    setCustomTabs(ts => ts.filter(t => t.id !== tabId));
    setActiveTab('alerta');
  };

  return (
    <div className={`rrtv-page${immersive ? ' immersive' : ''}`}>

      {/* Animated depth background (Silk) — reused from the Stocks UX */}
      <div className="rrtv-silk" aria-hidden="true">
        <Silk speed={18} scale={1.3} color="#6660fe" noiseIntensity={1.6} rotation={0.12} />
      </div>

      <div className="rrtv-shell">

      {/* App header — auto-collapses in immersive mode to kill occlusion */}
      <div className="rrtv-appheader">
        <Header theme={theme} isDark={isDark} toggleTheme={() => setIsDark(!isDark)}
          topTickers={topTickers} marketData={marketData} />
      </div>

      {/* ============ RR TV HEADER + TABS ============ */}
      <header className="rrtv-header">
        <div className="rrtv-brand">
          <LogoRRTV className="rrtv-logo-svg" />
          <div className="rrtv-brand-text">
            <span className="rrtv-brand-sub">PLATAFORMA · TEMPO REAL</span>
            <span className="rrtv-brand-main">RELATÓRIO RESERVADO · RR MAGMA</span>
          </div>
        </div>

        {/* Top-level tabs */}
        <nav className="rrtv-tabs" role="tablist">
          <button
            className={`rrtv-tab${activeTab === 'alerta' ? ' active' : ''}`}
            onClick={() => setActiveTab('alerta')}
          >
            <Wifi size={13} /> Alerta
          </button>
          <button
            className={`rrtv-tab${activeTab === 'research' ? ' active' : ''}`}
            onClick={() => setActiveTab('research')}
          >
            <LayoutGrid size={13} /> Research
          </button>
          {customTabs.map(t => (
            <button
              key={t.id}
              className={`rrtv-tab${activeTab === t.id ? ' active' : ''}`}
              onClick={() => setActiveTab(t.id)}
            >
              {t.name}
              <span className="rrtv-tab-close" onClick={(e) => { e.stopPropagation(); closeTab(t.id); }}>
                <X size={11} />
              </span>
            </button>
          ))}
          <button className="rrtv-tab rrtv-tab-new" onClick={addTab} title="Novo painel">
            <Plus size={13} /> Novo
          </button>
        </nav>

        <div className="rrtv-header-right">
          <div className={`rrtv-status ${isConnected ? 'connected' : 'disconnected'}`}>
            {isConnected ? <Wifi size={14} /> : <WifiOff size={14} />}
            <span>{isConnected ? 'LIVE' : 'OFFLINE'}</span>
          </div>
          <div className="rrtv-updated">Atualizado: {fmtTime(lastUpdate)}</div>
          <button className="rrtv-refresh" onClick={fetchData} aria-label="refresh">
            <RefreshCw size={16} />
          </button>
          <button className={`rrtv-immersive-btn${immersive ? ' active' : ''}`}
            onClick={() => setImmersive(v => !v)}
            title={immersive ? 'Sair do modo imersivo' : 'Modo imersivo'} aria-label="modo imersivo">
            {immersive ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
          </button>
        </div>
      </header>

      {/* ============ TAB CONTENT — kept mounted so state persists across tab switches ============ */}
      <div className="rrtv-tabview" hidden={activeTab !== 'alerta'}>
        <AlertaBoard data={boardData} isMobile={layout.isMobile} onImmersive={setImmersive} />
      </div>

      <div className="rrtv-tabview" hidden={activeTab !== 'research'}>
        <div className="rrtv-tabpane"><ResearchTab onImmersive={setImmersive} /></div>
      </div>

      {customTabs.map(t => (
        <div key={t.id} className="rrtv-tabview" hidden={activeTab !== t.id}>
          <div className="rrtv-tabpane">
            <CustomPane tab={t} marketData={marketData} onChange={updateTab} />
          </div>
        </div>
      ))}

      </div>{/* .rrtv-shell */}
    </div>
  );
}

export default RRTVMarketPage;
